import type { AppDeveloperProfileShareCapability, MyAppStoreAccess } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.interface";
import { reactive } from "vue";
import { fetchMyAppStoreAccess } from "./myAppStoreAccess.api";

/**
 * What the signed-in person reaches per organisation through shares (installs they own or were given, and a
 * developer profile share), as `GET /api/clients/{client}/my_app_store_access` reports it. Loaded once per
 * organisation and kept for the session; the App Store gates read it synchronously once it is loaded.
 */
const accessByClient = reactive(new Map<string, MyAppStoreAccess>());
const pending = new Map<string, Promise<MyAppStoreAccess | null>>();
// Bumped on every reset: a load that started for the previous person must not store its answer for the next one.
let generation = 0;

export function myAppStoreAccessFor(clientIri: string | null | undefined): MyAppStoreAccess | null {
  if (!clientIri) {
    return null;
  }

  return accessByClient.get(clientIri) ?? null;
}

export function developerCapabilityFor(clientIri: string | null | undefined): AppDeveloperProfileShareCapability | null {
  return myAppStoreAccessFor(clientIri)?.developerProfileCapability ?? null;
}

export function hasPersonalInstalls(clientIri: string | null | undefined): boolean {
  return (myAppStoreAccessFor(clientIri)?.personalInstallCount ?? 0) > 0;
}

/**
 * Loads the access of one organisation. A failed load counts as no access through shares: the person's own
 * permissions still decide, so a share-only person is shown the "no access" state rather than a broken page.
 */
export function loadMyAppStoreAccess(clientIri: string | null | undefined, { force = false } = {}): Promise<MyAppStoreAccess | null> {
  if (!clientIri) {
    return Promise.resolve(null);
  }

  const cached = accessByClient.get(clientIri);
  if (cached && !force) {
    return Promise.resolve(cached);
  }

  const inFlight = pending.get(clientIri);
  if (inFlight && !force) {
    return inFlight;
  }

  const startedIn = generation;
  const request = fetchMyAppStoreAccess(clientIri)
    .then((data) => {
      if (startedIn !== generation) {
        return null;
      }
      accessByClient.set(clientIri, data);
      return data;
    })
    .catch(() => null)
    .finally(() => {
      if (pending.get(clientIri) === request) {
        pending.delete(clientIri);
      }
    });
  pending.set(clientIri, request);

  return request;
}

/**
 * Forgets what was loaded, e.g. after a share changed or another person signed in (impersonation start or stop,
 * logout, account switch). A load still in flight then stores nothing.
 */
export function resetMyAppStoreAccess(): void {
  generation += 1;
  accessByClient.clear();
  pending.clear();
}
